import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { getSearchFromPath } from '@/utils/urlUtils';

import { useRouter } from '@/hooks/useRouter';
import {
  areFlagsEqual,
  defaultFlags,
  parseThirdPartyFlagsFromCookie,
  parseThirdPartyFlagsFromSearch,
  seedThirdPartyFlags,
  shouldDisableAlgoliaInsights,
  shouldDisableCampaignTracker,
  shouldDisableGtm,
  shouldDisableOneTrust,
  shouldDisableSentry,
  type ThirdPartyFlags,
  writeThirdPartyFlagsCookie,
} from '@/lib/thirdPartyFlags';

/**
 * React context for third-party disable flags.
 *
 * State is owned entirely by this provider — no module-level globals,
 * On every client-side navigation the provider re-parses router.asPath.
 * If the params disappear (e.g. user navigates to a URL without them),
 * flags fall back to defaultFlags ("all enabled") — no stale opt-outs.
 */
const ThirdPartyFlagsContext = createContext<ThirdPartyFlags>(defaultFlags);

export type ThirdPartyFlagsProviderProps = {
  children: ReactNode;
  /** Flags parsed server-side in getInitialProps — avoids a client flash. */
  initialFlags: ThirdPartyFlags;
};

export const ThirdPartyFlagProvider = ({
  children,
  initialFlags,
}: ThirdPartyFlagsProviderProps) => {
  const router = useRouter();
  const [flags, setFlags] = useState<ThirdPartyFlags>(initialFlags);

  // Keep window.__DISABLE_SENTRY__ in sync whenever flags change.
  useEffect(() => {
    seedThirdPartyFlags(flags);
  }, [flags]);

  // Re-parse on every navigation. The session cookie is the fallback so the
  // opt-out survives normal navigation (link clicks) and redirects that don't
  // carry the query: URL params take precedence per-key, anything absent
  // inherits the cookie. An explicit `disable_x=0` overrides the cookie
  // (reset), and the resolved flags are written back to the session cookie
  // (empty → cleared).
  useEffect(() => {
    const search = getSearchFromPath(router.asPath);
    const cookieFlags = parseThirdPartyFlagsFromCookie(
      typeof document !== 'undefined' ? document.cookie : null,
      defaultFlags,
    );
    const nextFlags = parseThirdPartyFlagsFromSearch(search, cookieFlags);
    if (!areFlagsEqual(nextFlags, cookieFlags)) {
      writeThirdPartyFlagsCookie(nextFlags);
    }
    setFlags((prev) => (areFlagsEqual(prev, nextFlags) ? prev : nextFlags));
  }, [router.asPath]);

  return (
    <ThirdPartyFlagsContext.Provider value={flags}>
      {children}
    </ThirdPartyFlagsContext.Provider>
  );
};

export const useThirdPartyFlags = (): ThirdPartyFlags =>
  useContext(ThirdPartyFlagsContext);

/** Returns false when Algolia analytics (insights + events) should be suppressed. */
export const useAlgoliaInsightsEnabled = (): boolean => {
  const flags = useThirdPartyFlags();
  return !shouldDisableAlgoliaInsights(flags);
};

/** Returns false when GTM / dataLayer should be suppressed. */
export const useGtmEnabled = (): boolean => {
  const flags = useThirdPartyFlags();
  return !shouldDisableGtm(flags);
};

/** Returns false when CampaignTracker init should be suppressed. */
export const useCampaignTrackerEnabled = (): boolean => {
  const flags = useThirdPartyFlags();
  return !shouldDisableCampaignTracker(flags);
};

/** Returns false when Sentry error reporting should be suppressed. */
export const useSentryEnabled = (): boolean => {
  const flags = useThirdPartyFlags();
  return !shouldDisableSentry(flags);
};

/** Returns false when OneTrust cookie consent scripts should be suppressed. */
export const useOneTrustEnabled = (): boolean => {
  const flags = useThirdPartyFlags();
  return !shouldDisableOneTrust(flags);
};
